import {
  Button,
  CircularProgress,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  Text,
} from '@civilgrid/ui/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { resetDemoOrg as apiResetDemoOrg, getDemoOrgs } from '~/api/internal';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Notify } from '~/components2/Notify';

// Super-admin tool: physically wipes a demo org's projects and restores the golden image.
// The picker lists ONLY demo orgs (server-side isDemoOrg filter), and a type-to-confirm dialog
// guards the destructive dispatch. The reset itself runs as a background Inngest job.
export default function ResetDemoOrg() {
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const {
    data: demoOrgs = [],
    isLoading,
    isError: loadError,
  } = useQuery({
    queryKey: ['internal', 'demoOrgs'],
    queryFn: getDemoOrgs,
    // Don't refetch while the operator is mid-flow: a focus-driven refetch could drop the selected
    // org out from under an open confirm dialog. A changed target is still caught server-side (the
    // reset endpoint 404s a mismatch and the client tells the operator to reload).
    refetchOnWindowFocus: false,
  });

  const selectedOrg = demoOrgs.find((o) => o.id === selectedOrgId) ?? null;

  const resetMutation = useMutation({
    // Take the whole org (not just the id) so the success toast reads the org that was actually
    // dispatched, not `selectedOrg` from the render closure (which a demoOrgs refetch could change).
    mutationFn: (org: { id: string; name: string }) => apiResetDemoOrg(org.id),
    onSuccess: ({ eventId }, org) => {
      capturePosthogEvent(POSTHOG_EVENTS.demoOrgResetRequested, { organization_id: org.id });
      Notify.info({
        // The job dispatches with singleton `skip`, so a request is silently dropped when a reset
        // is already running. Keep the copy honest about that rather than claiming it always starts.
        message: `Reset requested for ${org.name} (job ${eventId}). It runs in the background, and is skipped if a reset is already running.`,
      });
      // Clear the selection so the danger button disarms; the toast is easy to miss, and leaving
      // the org selected invites an accidental second dispatch of a destructive wipe-and-restore.
      setSelectedOrgId('');
      setIsDialogOpen(false);
    },
    onError: (e) => {
      console.error('Error dispatching demo org reset:', e);
      // Distinguish the endpoint's failure modes: a 404 means the golden-image target changed since
      // the picker loaded (reload to recover); a 5xx means the manifest read failed (transient, worth
      // retrying); a 400 carries a server-supplied reason worth surfacing verbatim.
      const response = (e as { response?: { status?: number; data?: { reason?: string } } })?.response;
      const status = response?.status;
      const message =
        status === 404
          ? 'This org is no longer the current reset target. Reload the page and try again.'
          : status !== undefined && status >= 500
            ? 'Could not read the golden image (temporary issue). Please try again in a moment.'
            : (response?.data?.reason ?? 'Error starting demo org reset. Please try again.');
      Notify.error({ message });
    },
  });

  const openConfirm = () => {
    setConfirmText('');
    setIsDialogOpen(true);
  };

  const onConfirm = () => {
    if (!selectedOrg) return;
    resetMutation.mutate(selectedOrg);
  };

  return (
    <div className="space-y-4 mb-10">
      <Text className="block text-sm text-grey-70">
        Physically deletes every project in the selected demo org, then restores the curated golden image. Only demo
        orgs are listed, and the reset runs as a background job.
      </Text>

      {isLoading && <CircularProgress indeterminate />}
      {loadError && (
        <Text className="block text-sm text-red-60" data-testid="reset-demo-org-load-error">
          Couldn't load demo orgs.
        </Text>
      )}

      {!isLoading && !loadError && (
        <>
          <div className="flex items-center space-x-2">
            <label htmlFor="reset-demo-org-select" className="font-semibold">
              Demo org:
            </label>
            <select
              id="reset-demo-org-select"
              data-testid="reset-demo-org-select"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
            >
              <option value="">Select a demo org…</option>
              {demoOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
          {demoOrgs.length === 0 && (
            <Text className="block text-sm text-grey-60" data-testid="reset-demo-org-empty">
              No demo org has a golden image to restore in this environment. Export one first, then it will appear here.
            </Text>
          )}

          <Button
            type="button"
            variant="danger"
            data-testid="reset-demo-org-open"
            disabled={!selectedOrg || resetMutation.isPending}
            onClick={openConfirm}
          >
            Reset Demo Org
          </Button>
        </>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          title={selectedOrg ? `Reset ${selectedOrg.name}?` : 'Reset demo org?'}
          // Blocks dismissal mid-reset so closing can't hide that the destructive job was already dispatched.
          preventDismiss={resetMutation.isPending}
          dismissible={!resetMutation.isPending}
          className="z-[110] w-auto min-w-min max-w-[75vw] w-[30rem] max-w-[90vw]"
          overlayClassName="z-[100]"
          data-testid="reset-demo-org-confirm-dialog"
        >
          <DialogBody className="text-sm">
            <div className="flex flex-col gap-3" data-testid="reset-demo-org-dialog">
              <Text className="text-sm">
                This permanently deletes every project in <strong>{selectedOrg?.name}</strong> and rebuilds it from the
                golden image. It cannot be undone.
              </Text>
              <Text className="text-sm">
                Type the org name <strong>{selectedOrg?.name}</strong> to confirm:
              </Text>
              <input
                id="reset-demo-org-confirm-input"
                aria-label={selectedOrg ? `Type ${selectedOrg.name} to confirm` : 'Type the org name to confirm'}
                data-testid="reset-demo-org-confirm-input"
                className="border rounded px-2 py-1 text-sm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={selectedOrg?.name}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsDialogOpen(false)}
              disabled={resetMutation.isPending}
              data-testid="reset-demo-org-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={onConfirm}
              disabled={resetMutation.isPending || confirmText !== selectedOrg?.name}
              data-testid="reset-demo-org-confirm"
            >
              {resetMutation.isPending ? 'Starting…' : 'Reset Demo Org'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
